import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/vendas/pedidos-lista-page.tsx — Fase 12, Planos 02 e 04 (PV-01, PV-05, PV-06, D-37).
// Lista "Pedidos" (antes "Pedidos de Venda", D-24): barra de filtros fixa (busca, cliente, situação, prazo, encerrados) resolvida
// no servidor, situação derivada, saldo e pendências. Nenhum filtro ou situação é calculado aqui.
import { useCallback, useEffect, useState, type FormEvent, type MouseEvent } from 'react';
import { AlertTriangle, ArrowUp, CalendarX, ChevronsUpDown, Clock, FileSpreadsheet, FileText, Plus, RefreshCw, Search, X } from 'lucide-react';
import type { ClienteLookupPv, ListaPedidosVenda, PrazoFiltroPv, SituacaoPv } from '@pieltec/contracts';
import { ClienteCombobox } from '../../components/vendas/cliente-combobox';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Skeleton } from '../../components/ui/skeleton';
import { TablePagination } from '../../components/ui/table-pagination';
import { faixaUrgencia, iconeUrgencia } from '../../lib/kanban/apresentacao';
import { obterSessaoAtual } from '../../lib/session';
import {
  OPCOES_PRAZO_PV,
  SITUACOES_LISTA_PV,
  filtrosPvAtivos,
  filtrosPvVazios,
  nivelDaPrioridadePv,
  rotuloSituacaoPv,
  serializarFiltrosPv,
  situacaoPrazoPv,
  textoPendencias,
  type FiltrosPv,
} from '../../lib/vendas/filtros';
import { formatarData, formatarQuantidade } from '../../lib/vendas/formatacao';
import { RelatorioClienteDialog } from './relatorio/relatorio-cliente-dialog';
import { RelatoriosRecentesPainel } from './relatorio/relatorios-recentes-painel';
import { CampoSelect } from '../../components/ui/campo-select';

const TAMANHO_PADRAO = 50;

const ICONES_URGENCIA = { CalendarX, AlertTriangle, ArrowUp, Clock } as const;
const ROTULO_URGENCIA: Record<keyof typeof ICONES_URGENCIA, string> = {
  CalendarX: 'Prazo estourado',
  AlertTriangle: 'Prioridade urgente',
  ArrowUp: 'Prioridade alta',
  Clock: 'Prazo próximo',
};

function contagem(total: number): string {
  return total === 1 ? '1 pedido' : `${total} pedidos`;
}

function abrirPv(id: string): void {
  window.location.assign(`/vendas/pedidos/detalhe?id=${encodeURIComponent(id)}`);
}

export function PedidosListaPage() {
  const [dados, setDados] = useState<ListaPedidosVenda | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [buscaDigitada, setBuscaDigitada] = useState('');
  const [filtros, setFiltros] = useState<FiltrosPv>(filtrosPvVazios);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(TAMANHO_PADRAO);
  const [podeEditar, setPodeEditar] = useState(false);
  const [relatorioAberto, setRelatorioAberto] = useState(false);
  const [clienteDoPainel, setClienteDoPainel] = useState<ClienteLookupPv | null>(null);
  const [atualizacaoPainel, setAtualizacaoPainel] = useState(0);

  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPodeEditar(sessao.autenticado && sessao.usuario.permissions.includes('pv:editar'));
    });
    return () => {
      vivo = false;
    };
  }, []);

  const carregar = useCallback(async (sinal?: { vivo: boolean }) => {
    setCarregando(true);
    setErro(false);
    try {
      const res = await fetch(`/api/v1/vendas/pedidos?${serializarFiltrosPv(filtros, { pagina, tamanhoPagina })}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as ListaPedidosVenda;
      if (!sinal || sinal.vivo) setDados(json);
    } catch {
      if (!sinal || sinal.vivo) setErro(true);
    } finally {
      if (!sinal || sinal.vivo) setCarregando(false);
    }
  }, [filtros, pagina, tamanhoPagina]);

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  function alterarFiltros(parcial: Partial<FiltrosPv>): void {
    setFiltros((atual) => ({ ...atual, ...parcial }));
    setPagina(1);
  }

  function aplicarBusca(event: FormEvent): void {
    event.preventDefault();
    alterarFiltros({ busca: buscaDigitada.trim() });
  }

  function limparFiltros(): void {
    setBuscaDigitada('');
    setFiltros(filtrosPvVazios());
    setPagina(1);
  }

  function alternarSituacao(situacao: SituacaoPv, marcada: boolean): void {
    const proximas = marcada ? [...filtros.situacoes, situacao] : filtros.situacoes.filter((atual) => atual !== situacao);
    alterarFiltros({ situacoes: proximas });
  }

  function cliqueNaLinha(event: MouseEvent<HTMLTableRowElement>, id: string): void {
    if ((event.target as HTMLElement).closest('a,button')) return;
    abrirPv(id);
  }

  const total = dados?.total ?? 0;
  const comFiltro = filtrosPvAtivos(filtros);
  const semResultadoComFiltro = !carregando && !erro && total === 0 && comFiltro;
  const vazio = !carregando && !erro && total === 0 && !comFiltro;
  const resumoSituacoes =
    filtros.situacoes.length === 0
      ? 'Todas as situações'
      : filtros.situacoes.length === 1
        ? rotuloSituacaoPv(filtros.situacoes[0] as SituacaoPv).texto
        : `${filtros.situacoes.length} situações`;

  return (
    <div className="mx-auto w-full max-w-7xl p-6 flex flex-col gap-6">
      <Card>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold leading-tight">Pedidos</h1>
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {dados ? contagem(dados.total) : ' '}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="secondary" onClick={() => setRelatorioAberto(true)}>
              <FileText aria-hidden="true" />
              Relatório ao cliente
            </Button>
            {podeEditar && (
              <>
                <Button asChild variant="secondary">
                  <a href="/vendas/pedidos/importar">
                    <FileSpreadsheet aria-hidden="true" />
                    Importar planilha
                  </a>
                </Button>
                <Button asChild>
                  <a href="/vendas/pedidos/novo">
                    <Plus aria-hidden="true" />
                    Novo PV
                  </a>
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <RelatoriosRecentesPainel clienteId={clienteDoPainel?.id ?? null} atualizacao={atualizacaoPainel} />

      <RelatorioClienteDialog
        aberto={relatorioAberto}
        onAbertoChange={setRelatorioAberto}
        clienteInicial={filtros.cliente}
        onGerado={(_relatorio, cliente) => {
          setClienteDoPainel(cliente);
          setAtualizacaoPainel((n) => n + 1);
        }}
      />

      <div className="sticky top-0 z-10 flex flex-col gap-3 border-b bg-background/95 py-3 backdrop-blur" role="group" aria-label="Filtros da lista">
        <form onSubmit={aplicarBusca} role="search" className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              type="search"
              aria-label="Buscar pedidos"
              placeholder="Buscar por nº do PV, nº do pedido do cliente ou cliente"
              value={buscaDigitada}
              onChange={(e) => setBuscaDigitada(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="secondary">
            Buscar
          </Button>
        </form>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-[16rem] flex-col gap-1">
            <label htmlFor="filtro-cliente" className="text-xs font-medium text-slate-600">
              Cliente
            </label>
            <div className="flex items-center gap-1">
              <div className="flex-1">
                <ClienteCombobox value={filtros.cliente} onChange={(cliente) => alterarFiltros({ cliente })} id="filtro-cliente" />
              </div>
              {filtros.cliente && (
                <Button type="button" variant="ghost" size="icon" aria-label="Limpar cliente" onClick={() => alterarFiltros({ cliente: null })}>
                  <X aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-600">Situação</span>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  className="ui-control flex min-h-[var(--control-height,2rem)] min-w-[12rem] items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                >
                  <span className="truncate">{resumoSituacoes}</span>
                  <ChevronsUpDown className="ml-2 size-4 shrink-0 text-slate-400" aria-hidden="true" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" aria-label="Situações">
                <div className="flex flex-col gap-1">
                  {SITUACOES_LISTA_PV.map((situacao) => {
                    const idCampo = `filtro-situacao-${situacao}`;
                    return (
                      <label key={situacao} htmlFor={idCampo} className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 rounded-md px-2 text-sm hover:bg-slate-100">
                        <Checkbox id={idCampo} checked={filtros.situacoes.includes(situacao)} onCheckedChange={(marcada) => alternarSituacao(situacao, marcada === true)} />
                        {rotuloSituacaoPv(situacao).texto}
                      </label>
                    );
                  })}
                  {filtros.situacoes.length > 0 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => alterarFiltros({ situacoes: [] })}>
                      Limpar situações
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="filtro-prazo" className="text-xs font-medium text-slate-600">
              Prazo
            </label>
            <CampoSelect
              id="filtro-prazo"
              value={filtros.prazo}
              onValueChange={(valor) => alterarFiltros({ prazo: valor as PrazoFiltroPv })}
              options={OPCOES_PRAZO_PV.map((opcao) => ({ value: opcao.valor, label: opcao.rotulo }))}
              className="w-56"
            />
          </div>

          {filtros.prazo === 'PERIODO' && (
            <>
              <div className="flex flex-col gap-1">
                <label htmlFor="filtro-prazo-de" className="text-xs font-medium text-slate-600">
                  De
                </label>
                <DatePicker id="filtro-prazo-de" value={filtros.prazoDe} onValueChange={(value) => alterarFiltros({ prazoDe: value })} />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="filtro-prazo-ate" className="text-xs font-medium text-slate-600">
                  Até
                </label>
                <DatePicker id="filtro-prazo-ate" value={filtros.prazoAte} onValueChange={(value) => alterarFiltros({ prazoAte: value })} />
              </div>
            </>
          )}

          <label htmlFor="filtro-encerrados" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
            <Checkbox id="filtro-encerrados" checked={filtros.incluirEncerrados} onCheckedChange={(marcada) => alterarFiltros({ incluirEncerrados: marcada === true })} />
            Incluir concluídos e cancelados
          </label>

          {comFiltro && (
            <Button type="button" variant="ghost" onClick={limparFiltros}>
              Limpar filtros
            </Button>
          )}
        </div>
      </div>

      {erro ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">
              Não foi possível carregar os pedidos. Verifique a conexão e tente novamente.
            </p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : carregando && !dados ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando pedidos">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : vazio ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum pedido</h2>
            <p className="max-w-xl text-sm text-muted-foreground">
              Crie um pedido do cliente manualmente, importe uma planilha do cliente ou use &quot;Criar PV desta nota&quot; na conferência de uma NF.
            </p>
          </CardContent>
        </Card>
      ) : semResultadoComFiltro ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum PV encontrado</h2>
            <p className="text-sm text-muted-foreground">Ajuste ou limpe os filtros.</p>
            <Button type="button" variant="outline" onClick={limparFiltros}>
              Limpar filtros
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden py-0 gap-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">PV</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Cliente</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Pedido do cliente</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Prazo</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-right">Itens</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Situação</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-right">Saldo</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Pendências</th>
                <th scope="col" className="px-3 py-2">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {dados?.itens.map((pv) => {
                const nivel = nivelDaPrioridadePv(pv.prioridade);
                const prazo = situacaoPrazoPv(pv.prazoSolicitado, pv.situacao);
                const nomeIcone = iconeUrgencia(nivel, prazo);
                const Icone = nomeIcone ? ICONES_URGENCIA[nomeIcone] : null;
                const situacao = rotuloSituacaoPv(pv.situacao);
                return (
                  <tr key={pv.id} onClick={(event) => cliqueNaLinha(event, pv.id)} className="cursor-pointer border-t hover:bg-slate-50">
                    <td className="relative py-2 pl-5 pr-3 font-semibold tabular-nums">
                      <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1.5 ${faixaUrgencia({ nivel, prazo })}`} />
                      <span className="flex items-center gap-2">
                        {Icone && nomeIcone && (
                          <span title={ROTULO_URGENCIA[nomeIcone]}>
                            <Icone className="size-4 shrink-0 text-slate-600" aria-hidden="true" />
                            <span className="sr-only">{ROTULO_URGENCIA[nomeIcone]}</span>
                          </span>
                        )}
                        {pv.numero}
                      </span>
                    </td>
                    <td className="px-3 py-2 max-w-[24rem] truncate">{pv.clienteNome}</td>
                    <td className="px-3 py-2">{pv.numeroPedidoCliente ?? '—'}</td>
                    <td className="px-3 py-2 tabular-nums">{formatarData(pv.prazoSolicitado)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pv.totalItens}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${situacao.classes}`}>{situacao.texto}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatarQuantidade(pv.saldoTotal, '')}</td>
                    <td className="px-3 py-2">
                      <span className={pv.totalPendencias > 0 ? 'font-medium text-amber-700' : 'text-slate-500'}>{textoPendencias(pv.totalPendencias)}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button asChild variant="outline" size="sm">
                        <a href={`/vendas/pedidos/detalhe?id=${encodeURIComponent(pv.id)}`} aria-label={`Abrir ${pv.numero}`}>
                          Abrir
                        </a>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            totalItems={total}
            onPageChange={setPagina}
            onPageSizeChange={(tamanho) => {
              setTamanhoPagina(tamanho);
              setPagina(1);
            }}
            itemLabel="pedidos"
          />
        </Card>
      )}
    </div>
  );
}
